import React, { useState, useEffect, useCallback } from 'react';
import { 
  Edit3, Search, PlusCircle, RefreshCw, Eye, Trash2, Image, 
  CheckCircle, X, TrendingUp, Clock, Zap, FileText, Send,
  ChevronDown, ChevronUp, MessageSquare, Sparkles, Save
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import toast from 'react-hot-toast';
import BlogEditor from './BlogEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const colors = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  pink: '#ec4899'
};

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div 
        className="p-3 rounded-full" 
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
    </div>
  </div>
);

const BlogAdminPanel = () => {
  // View state
  const [view, setView] = useState('list'); // 'list', 'suggestions', 'editor', 'preview'
  
  // Stats state
  const [blogStats, setBlogStats] = useState(null);
  
  // Posts list state
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsLoading, setPostsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Suggestions state
  const [seedKeyword, setSeedKeyword] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Editor state
  const [editingPost, setEditingPost] = useState(null);
  const [previewPost, setPreviewPost] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  
  // Generation state
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState('');
  
  // AI adjustment state
  const [aiAdjustment, setAiAdjustment] = useState('');
  const [applyingAdjustment, setApplyingAdjustment] = useState(false);
  
  // Generation modal (legacy)
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateOptions, setGenerateOptions] = useState({ count: 5, category: '', dry_run: false });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generationTaskId, setGenerationTaskId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);

  // Fetch blog stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/blog/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogStats(response.data);
    } catch (error) {
      console.error('Failed to fetch blog stats:', error);
    }
  }, []);

  // Fetch posts from database
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (search) params.set('search', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (statusFilter) params.set('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/admin/blog/drafts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data.posts);
      setPostsTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      // Fallback to legacy endpoint
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: perPage.toString()
        });
        if (search) params.set('search', search);
        if (categoryFilter) params.set('category', categoryFilter);
        
        const response = await axios.get(`${API_URL}/admin/blog/posts?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPosts(response.data.posts);
        setPostsTotal(response.data.total);
      } catch (legacyError) {
        console.error('Legacy posts fetch failed:', legacyError);
      }
    } finally {
      setPostsLoading(false);
    }
  }, [page, perPage, search, categoryFilter, statusFilter]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/blog/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  // Fetch keyword suggestions
  const fetchSuggestions = async () => {
    if (!seedKeyword.trim()) return;
    
    setSuggestionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/admin/blog/suggestions?seed=${encodeURIComponent(seedKeyword)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      toast.error('Failed to fetch keyword suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Generate content preview
  const generatePreview = async (query) => {
    setGeneratingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/blog/generate-preview`,
        { query, category: categoryFilter || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Set up new post in editor
      setEditingPost({
        id: null,
        title: response.data.title,
        slug: response.data.suggested_slug,
        content: response.data.content,
        excerpt: response.data.excerpt,
        meta_description: response.data.meta_description,
        keywords: response.data.keywords.join(', '),
        category: response.data.category,
        read_time: response.data.read_time,
        image_prompts: response.data.image_prompts,
        status: 'draft',
        search_query: query
      });
      setView('editor');
      toast.success('Content generated! Review and edit before saving.');
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate content');
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Apply AI adjustment to content
  const applyAiAdjustment = async () => {
    if (!editingPost || !aiAdjustment.trim()) return;
    
    setApplyingAdjustment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/blog/adjust-content`,
        {
          content: editingPost.content,
          instruction: aiAdjustment,
          title: editingPost.title
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEditingPost(prev => ({ ...prev, content: response.data.content }));
      setAiAdjustment('');
      toast.success('AI adjustment applied!');
    } catch (error) {
      console.error('Failed to apply adjustment:', error);
      toast.error('Failed to apply AI adjustment');
    } finally {
      setApplyingAdjustment(false);
    }
  };

  // Save draft
  const saveDraft = async () => {
    if (!editingPost) return;
    
    setSaveLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingPost.id) {
        // Update existing
        await axios.put(
          `${API_URL}/admin/blog/drafts/${editingPost.id}`,
          {
            title: editingPost.title,
            slug: editingPost.slug,
            content: editingPost.content,
            excerpt: editingPost.excerpt,
            meta_description: editingPost.meta_description,
            keywords: editingPost.keywords,
            category: editingPost.category,
            featured_image: editingPost.featured_image,
            images: editingPost.images
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post updated!');
      } else {
        // Create new
        const response = await axios.post(
          `${API_URL}/admin/blog/drafts`,
          {
            title: editingPost.title,
            content: editingPost.content,
            excerpt: editingPost.excerpt,
            meta_description: editingPost.meta_description,
            keywords: editingPost.keywords,
            category: editingPost.category,
            featured_image: editingPost.featured_image,
            images: editingPost.images,
            search_query: editingPost.search_query
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEditingPost(prev => ({ ...prev, id: response.data.id, slug: response.data.slug }));
        toast.success('Draft saved!');
      }
      fetchPosts();
      fetchStats();
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaveLoading(false);
    }
  };

  // Publish post
  const publishPost = async (postId) => {
    setPublishLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/blog/publish/${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post published!');
      fetchPosts();
      fetchStats();
      if (editingPost?.id === postId) {
        setEditingPost(prev => ({ ...prev, status: 'published' }));
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish post');
    } finally {
      setPublishLoading(false);
    }
  };

  // Unpublish post
  const unpublishPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/blog/unpublish/${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post unpublished');
      fetchPosts();
      fetchStats();
    } catch (error) {
      console.error('Failed to unpublish:', error);
      toast.error('Failed to unpublish post');
    }
  };

  // Delete post
  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/blog/drafts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete post');
    }
  };

  // Generate images for post
  const generateImages = async (postId) => {
    setGeneratingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/blog/generate-images/${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPosts();
      if (editingPost?.id === postId) {
        setEditingPost(prev => ({
          ...prev,
          images: response.data.images,
          featured_image: response.data.images?.[0]?.url
        }));
      }
    } catch (error) {
      console.error('Failed to generate images:', error);
      toast.error('Failed to generate images');
    } finally {
      setGeneratingImages(false);
    }
  };

  // Legacy: Generate posts (batch)
  const generateBlogPosts = async () => {
    setGenerateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/blog/generate`,
        generateOptions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGenerationTaskId(response.data.task_id);
      setShowGenerateModal(false);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to start generation:', error);
      toast.error('Failed to start blog generation');
    } finally {
      setGenerateLoading(false);
    }
  };

  // Load post for editing
  const editPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/blog/drafts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPost(response.data);
      setView('editor');
    } catch (error) {
      console.error('Failed to load post:', error);
      toast.error('Failed to load post');
    }
  };

  // Poll generation status
  useEffect(() => {
    if (!generationTaskId) return;
    
    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/admin/blog/generate/${generationTaskId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGenerationStatus(response.data);
        
        if (response.data.status !== 'running') {
          setGenerationTaskId(null);
          fetchPosts();
          fetchStats();
        }
      } catch (error) {
        console.error('Failed to poll status:', error);
      }
    };
    
    const interval = setInterval(pollStatus, 5000);
    pollStatus();
    
    return () => clearInterval(interval);
  }, [generationTaskId]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchPosts();
    fetchCategories();
  }, []);

  // Refetch posts when filters change
  useEffect(() => {
    fetchPosts();
  }, [page, search, categoryFilter, statusFilter, fetchPosts]);

  // Render suggestions view
  const renderSuggestionsView = () => (
    <div className="space-y-6">
      {/* Custom Title Input */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-sm p-6 border border-indigo-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Post with Custom Title</h3>
        <p className="text-sm text-gray-600 mb-4">Enter your own title idea and generate a complete blog post</p>
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Edit3 size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
            <input
              type="text"
              placeholder="Enter your title idea (e.g., 'How to Backup Instagram Stories Before They Disappear')"
              value={selectedQuery}
              onChange={(e) => setSelectedQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && selectedQuery.trim() && generatePreview(selectedQuery)}
              className="w-full pl-10 pr-4 py-3 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
            />
          </div>
          <button
            onClick={() => selectedQuery.trim() && generatePreview(selectedQuery)}
            disabled={generatingPreview || !selectedQuery.trim()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center font-medium"
          >
            {generatingPreview ? (
              <RefreshCw className="animate-spin h-4 w-4" />
            ) : (
              <>
                <Sparkles size={16} className="mr-2" />
                Generate Post
              </>
            )}
          </button>
        </div>
      </div>

      {/* Search for keywords */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Or Find Topics from DataForSEO</h3>
        <p className="text-sm text-gray-600 mb-4">Search for trending keywords and questions to write about</p>
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Enter a seed keyword (e.g., 'instagram backup')"
              value={seedKeyword}
              onChange={(e) => setSeedKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchSuggestions()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={fetchSuggestions}
            disabled={suggestionsLoading || !seedKeyword.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
          >
            {suggestionsLoading ? (
              <RefreshCw className="animate-spin h-4 w-4" />
            ) : (
              <>
                <Sparkles size={16} className="mr-2" />
                Get Suggestions
              </>
            )}
          </button>
        </div>
      </div>

      {/* Keyword Suggestions */}
      {suggestions && (
        <>
          {/* Keywords with volume */}
          {suggestions.keywords?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Keyword Suggestions ({suggestions.keywords.length})
              </h3>
              <div className="space-y-2">
                {suggestions.keywords.map((kw, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setSelectedQuery(kw.keyword);
                      generatePreview(kw.keyword);
                    }}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{kw.keyword}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Vol: {kw.search_volume?.toLocaleString() || 'N/A'}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          kw.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                          kw.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {kw.difficulty}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        generatePreview(kw.keyword);
                      }}
                      disabled={generatingPreview}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {generatingPreview && selectedQuery === kw.keyword ? (
                        <RefreshCw className="animate-spin h-4 w-4" />
                      ) : (
                        'Generate'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* People Also Ask */}
          {suggestions.questions?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MessageSquare size={20} className="mr-2" />
                People Also Ask ({suggestions.questions.length})
              </h3>
              <div className="space-y-2">
                {suggestions.questions.map((q, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{q.question}</p>
                        {q.answer_snippet && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{q.answer_snippet}</p>
                        )}
                      </div>
                      <button
                        onClick={() => generatePreview(q.question)}
                        disabled={generatingPreview}
                        className="ml-4 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Questions */}
          {suggestions.related_questions?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Questions</h3>
              <div className="flex flex-wrap gap-2">
                {suggestions.related_questions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => generatePreview(q)}
                    disabled={generatingPreview}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-indigo-100 hover:text-indigo-700"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render editor view
  const renderEditorView = () => {
    if (!editingPost) return null;
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setView('list');
              setEditingPost(null);
            }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ChevronDown className="rotate-90 mr-1" size={20} />
            Back to Posts
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreviewPost(editingPost)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
            >
              <Eye size={16} className="mr-2" />
              Preview
            </button>
            <button
              onClick={saveDraft}
              disabled={saveLoading}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center disabled:opacity-50"
            >
              {saveLoading ? (
                <RefreshCw className="animate-spin h-4 w-4 mr-2" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Save Draft
            </button>
            {editingPost.id && editingPost.status !== 'published' && (
              <button
                onClick={() => publishPost(editingPost.id)}
                disabled={publishLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
              >
                {publishLoading ? (
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                ) : (
                  <Send size={16} className="mr-2" />
                )}
                Publish
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={editingPost.title || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <BlogEditor
                value={editingPost.content || ''}
                onChange={(content) => setEditingPost(prev => ({ ...prev, content }))}
                onAutoSave={saveDraft}
              />
            </div>

            {/* AI Adjustment */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl shadow-sm p-6 border border-purple-100">
              <div className="flex items-center mb-3">
                <Sparkles size={20} className="text-purple-600 mr-2" />
                <label className="block text-sm font-medium text-gray-700">AI Content Adjustment</label>
              </div>
              <p className="text-sm text-gray-600 mb-3">Ask Gemini to make specific changes to your content</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="e.g., 'Make the introduction more engaging' or 'Add more examples about Stories backup'"
                  value={aiAdjustment}
                  onChange={(e) => setAiAdjustment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && aiAdjustment.trim() && applyAiAdjustment()}
                  className="flex-1 px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                />
                <button
                  onClick={applyAiAdjustment}
                  disabled={applyingAdjustment || !aiAdjustment.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                >
                  {applyingAdjustment ? (
                    <RefreshCw className="animate-spin h-4 w-4" />
                  ) : (
                    <>
                      <Sparkles size={16} className="mr-2" />
                      Apply
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            {editingPost.id && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  editingPost.status === 'published' ? 'bg-green-100 text-green-800' :
                  editingPost.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {editingPost.status}
                </span>
              </div>
            )}

            {/* Slug */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">URL Slug</label>
              <input
                type="text"
                value={editingPost.slug || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, slug: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">/blog/{editingPost.slug}</p>
            </div>

            {/* Category */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={editingPost.category || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select category</option>
                <option value="backup_guide">Backup Guide</option>
                <option value="security">Security</option>
                <option value="recovery">Recovery</option>
                <option value="tutorial">Tutorial</option>
                <option value="industry_news">Industry News</option>
              </select>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
              <textarea
                value={editingPost.excerpt || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, excerpt: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Meta Description */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description
                <span className="text-gray-400 font-normal ml-2">
                  ({(editingPost.meta_description || '').length}/160)
                </span>
              </label>
              <textarea
                value={editingPost.meta_description || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, meta_description: e.target.value }))}
                rows={3}
                maxLength={160}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Keywords */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Keywords</label>
              <input
                type="text"
                value={editingPost.keywords || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, keywords: e.target.value }))}
                placeholder="keyword1, keyword2, keyword3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Images */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Images</label>
                {editingPost.id && (
                  <button
                    onClick={() => generateImages(editingPost.id)}
                    disabled={generatingImages}
                    className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
                  >
                    {generatingImages ? (
                      <RefreshCw className="animate-spin h-4 w-4 mr-1" />
                    ) : (
                      <Sparkles size={14} className="mr-1" />
                    )}
                    Generate Images
                  </button>
                )}
              </div>
              {editingPost.images?.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {editingPost.images.map((img, idx) => (
                    <div key={idx} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No images yet. Save the draft first, then generate images.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render posts list
  const renderPostsList = () => (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Edit3}
          title="Total Posts"
          value={blogStats?.total_posts || 0}
          subtitle={`${blogStats?.posts_this_week || 0} this week`}
          color={colors.primary}
        />
        <StatCard
          icon={Image}
          title="With AI Images"
          value={blogStats?.posts_with_generated_images || 0}
          subtitle={`${blogStats?.posts_with_images || 0} with any image`}
          color={colors.success}
        />
        <StatCard
          icon={Clock}
          title="Posts Today"
          value={blogStats?.posts_today || 0}
          subtitle={blogStats?.latest_post_date ? `Latest: ${new Date(blogStats.latest_post_date).toLocaleDateString()}` : 'No posts yet'}
          color={colors.warning}
        />
        <StatCard
          icon={TrendingUp}
          title="Categories"
          value={Object.keys(blogStats?.categories || {}).length}
          subtitle="Content types"
          color={colors.secondary}
        />
      </div>

      {/* Generation Status */}
      {generationStatus && generationStatus.status === 'running' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <RefreshCw className="animate-spin h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-800">Blog generation in progress...</p>
              <p className="text-xs text-blue-600">Started: {generationStatus.started_at ? new Date(generationStatus.started_at).toLocaleTimeString() : 'Unknown'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setView('suggestions')}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <PlusCircle size={16} className="mr-2" />
              New Post
            </button>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Zap size={16} className="mr-2" />
              Batch Generate
            </button>
            <button
              onClick={() => { fetchStats(); fetchPosts(); }}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search posts..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 w-48"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Category Chart */}
      {blogStats?.categories && Object.keys(blogStats.categories).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(blogStats.categories).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {Object.entries(blogStats.categories).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(colors)[index % Object.keys(colors).length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Posts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Blog Posts ({postsTotal})
          </h3>
        </div>

        {postsLoading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin h-8 w-8 text-indigo-600" />
          </div>
        ) : posts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{post.title}</div>
                      <div className="text-xs text-gray-500">/{post.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        post.status === 'published' ? 'bg-green-100 text-green-800' :
                        post.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {post.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {post.created_at ? new Date(post.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => editPost(post.id)}
                          className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>
                        {post.status === 'published' && (
                          <a
                            href={`/blog/${post.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye size={16} />
                          </a>
                        )}
                        {post.status === 'draft' && (
                          <button
                            onClick={() => publishPost(post.id)}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Publish"
                          >
                            <Send size={16} />
                          </button>
                        )}
                        {post.status === 'published' && (
                          <button
                            onClick={() => unpublishPost(post.id)}
                            className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Unpublish"
                          >
                            <ChevronDown size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => deletePost(post.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Edit3 className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500">No blog posts found</p>
            <button
              onClick={() => setView('suggestions')}
              className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <PlusCircle size={16} className="mr-2" />
              Create Your First Post
            </button>
          </div>
        )}

        {/* Pagination */}
        {postsTotal > perPage && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, postsTotal)} of {postsTotal}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {Math.ceil(postsTotal / perPage)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(postsTotal / perPage)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* View Navigation */}
      {view !== 'list' && (
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setView('list');
              setEditingPost(null);
            }}
            className="text-indigo-600 hover:text-indigo-700 flex items-center"
          >
            ← Back to Posts
          </button>
          {view === 'suggestions' && (
            <span className="text-gray-500">/ Find Topics</span>
          )}
          {view === 'editor' && (
            <span className="text-gray-500">/ {editingPost?.id ? 'Edit Post' : 'New Post'}</span>
          )}
        </div>
      )}

      {/* Render current view */}
      {view === 'list' && renderPostsList()}
      {view === 'suggestions' && renderSuggestionsView()}
      {view === 'editor' && renderEditorView()}

      {/* Preview Modal - Bright background, dark text for readability */}
      {previewPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h3 className="text-gray-900 font-semibold">Preview: {previewPost.title}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setEditingPost(previewPost);
                    setPreviewPost(null);
                    setView('editor');
                  }}
                  className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => setPreviewPost(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-80px)] bg-white">
              <article className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-a:text-indigo-600">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{previewPost.title}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-4 border-b border-gray-200">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{previewPost.category}</span>
                  <span>{previewPost.read_time}</span>
                </div>
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: previewPost.content }} 
                />
              </article>
            </div>
          </div>
        </div>
      )}

      {/* Generate Modal (Legacy) */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Batch Generate SEO Posts</h3>
                <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-500">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Posts</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={generateOptions.count}
                  onChange={(e) => setGenerateOptions({...generateOptions, count: parseInt(e.target.value) || 5})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category (Optional)</label>
                <select
                  value={generateOptions.category}
                  onChange={(e) => setGenerateOptions({...generateOptions, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Categories</option>
                  <option value="content_type">Content Type Backup</option>
                  <option value="niche_profession">Niche/Profession Specific</option>
                  <option value="problem_solution">Problem-Solution</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="dry_run"
                  checked={generateOptions.dry_run}
                  onChange={(e) => setGenerateOptions({...generateOptions, dry_run: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <label htmlFor="dry_run" className="ml-2 text-sm text-gray-700">Dry run (preview only)</label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={generateBlogPosts}
                disabled={generateLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
              >
                {generateLoading ? (
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                ) : (
                  <Zap size={16} className="mr-2" />
                )}
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdminPanel;

